/* eslint-disable react-hooks/exhaustive-deps */
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import {
  Button,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import TodayIcon from "@mui/icons-material/Today";
import RefreshIcon from "@mui/icons-material/Refresh";
import makeAnimated from "react-select/animated";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  useLocation,
  useNavigate,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid2";
import Select from "react-select";
import useGetResource from "@/hooks/useGetResources";
import DocumentDetail from "./DocumentDetail";
import useGetDetailledDocument from "@/hooks/useGetDetailledDocument";
import useDeleteData from "@/hooks/deleteHooks/useDeleteData";
import useNotificationStore from "@/hooks/useNotificationStore";
import { useSession } from "@/hooks/useSession";
import DeleteModal from "@/components/deleteModal";
import Toast from "@/components/toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import useGetMetadata from "@/hooks/useGetMetaData";
import { useTheme, alpha } from "@mui/material/styles";
import { formatDateTimeForAPI } from "@/lib/utils/dateUtils";
import { getMultiSelectStyles } from "@/lib/styles";
import { SearchContext } from "@/layouts/Layout";
import { useTranslation } from "react-i18next";

// Import shared components
import DocumentCard from "./components/shared/DocumentCard";
import MapDialog from "./components/shared/MapDialog";
import CalendarDialog from "./components/shared/CalendarDialog";
import SimopsDialog from "./components/SimopsDialog";
import DocumentsHeader from "./components/shared/DocumentsHeader";
import DocumentSummaryPopup from "./components/shared/DocumentSummaryPopup";

const animatedComponents = makeAnimated();

function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const { session } = useSession();
  const { mutateAsync: deleteData } = useDeleteData("/api/v1/workpermits", [
    "documents",
  ]);
  const queryClient = useQueryClient();
  const { selectedWorkPermitId, clearSelectedWorkPermitId } =
    useNotificationStore();
  const { getDetailledData } = useGetDetailledDocument();
  const { handleApiError, toast, setToast, handleToastClose } =
    useApiErrorHandler();
  const { SitesList, WorkFlows, isLoading: metadataLoading } = useGetMetadata();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { documentId: urlDocumentId } = useParams();
  const navigationWorkPermitId = location.state?.workPermitId;
  const { searchTerm, setSearchTerm, inputValue, setInputValue } =
    useContext(SearchContext);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const multiSelectStyles = getMultiSelectStyles(theme);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [summaryPopupOpen, setSummaryPopupOpen] = useState(false);
  const [summaryDocument, setSummaryDocument] = useState(null);

  // State declarations
  const [pageTokens, setPageTokens] = useState([null]);
  const [pages, setPages] = useState([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedWorkflows, setSelectedWorkflows] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const fetchedWorkPermitIds = useRef(new Set());
  const handledUrlDocumentId = useRef(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [todayFilterClicked, setTodayFilterClicked] = useState(0);
  const [isTodayFilterActive, setIsTodayFilterActive] = useState(false);
  const [initialTotalCount, setInitialTotalCount] = useState(null);
  const [simopsDialogOpen, setSimopsDialogOpen] = useState(false);
  // Filter workflows for documents
  const documentWorkflowOptions = useMemo(
    () =>
      WorkFlows?.filter((wf) => wf.target === "WORK_PERMIT")?.map((wf) => ({
        value: wf.id,
        label: wf.name,
      })) || [],
    [WorkFlows]
  );

  // Sync searchTerm with URL search param on initial load
  /*  useEffect(() => {
    const urlSearchTerm = searchParams.get("search") || "";
    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
    }
  }, [searchParams, searchTerm, setSearchTerm]); */

  // Replace your current useEffect that syncs with URL
  useEffect(() => {
    const urlSearch = searchParams.get("search"); // null if absent
    const value = urlSearch ?? ""; // normalize to empty string
    // Only do work if something actually changes
    if (value !== searchTerm || value !== inputValue) {
      setSearchTerm(value);
      setInputValue(value);
      // also reset pagination whenever the effective search changes
      setPageTokens([null]);
      setPages([]);
      setCurrentPageIndex(0);
      setInitialTotalCount(null);
    }
  }, [searchParams]); // <-- note: don't include searchTerm/inputValue here

  // Consolidated pagination reset effect
  useEffect(() => {
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  }, [searchTerm]);

  // Fetch status filters
  const { data: statusFilters, isLoading: isStatusLoading } = useGetResource(
    ["statusFilters"],
    "/api/v1/workpermits/status-filters"
  );

  const statusButtons = useMemo(() => {
    if (!statusFilters) return [];
    return statusFilters.map((status) => ({
      name: status.name,
      count: status.count,
      ids: status.ids,
    }));
  }, [statusFilters]);

  // Define currentPageToken
  const currentPageToken = pageTokens[currentPageIndex];

  // Construct query URL based on selected statuses, search term, sites, workflows, and dates
  const queryUrl = useMemo(() => {
    const baseUrl = "/api/v1/workpermits/search";
    const params = new URLSearchParams();
    if (selectedStatuses.length > 0) {
      selectedStatuses.forEach((status) => {
        status.ids.forEach((id) => params.append("statuses", id));
      });
    }
    if (searchTerm) params.append("search_term", searchTerm);
    if (selectedSites.length > 0) {
      selectedSites.forEach((site) => params.append("sites", site.value));
    }
    if (selectedWorkflows.length > 0) {
      selectedWorkflows.forEach((workflow) =>
        params.append("workflows", workflow.value)
      );
    }
    if (fromDate)
      params.append("from_datetime", formatDateTimeForAPI(fromDate));
    if (toDate)
      params.append("to_datetime", formatDateTimeForAPI(toDate, true));
    if (currentPageToken) params.append("cursor", currentPageToken);
    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
  }, [
    selectedStatuses,
    searchTerm,
    selectedSites,
    selectedWorkflows,
    fromDate,
    toDate,
    currentPageToken,
  ]);

  const {
    data: Documents,
    isLoading,
    isFetching,
    error,
    refetch,
    isPlaceholderData,
    isError,
  } = useGetResource(
    [
      "documents",
      selectedStatuses.map((s) => s.name).join(","),
      searchTerm,
      selectedSites.map((s) => s.value).join(","),
      selectedWorkflows.map((w) => w.value).join(","),
      fromDate,
      toDate,
      currentPageToken,
      todayFilterClicked,
    ],
    queryUrl,
    { enabled: true }
  );

  useEffect(() => {
    if (isPlaceholderData) return;
    if (isError) return;
    if (!Documents || !Array.isArray(Documents.records)) return;

    const sanitized = Documents.records.filter((r) => r && r.id);

    if (currentPageToken == null) {
      setPages([sanitized]);
      setCurrentPageIndex(0);
      setInitialTotalCount(
        typeof Documents.total_count === "number"
          ? Documents.total_count
          : Number(Documents.total_count) || 0
      );
    } else {
      setPages((prev) => {
        const existingIds = new Set(
          prev.flat().filter(Boolean).map((r) => r.id).filter(Boolean)
        );
        const filteredRecords = sanitized.filter((r) => !existingIds.has(r.id));
        const newPages = [...prev];
        while (newPages.length <= currentPageIndex) {
          newPages.push([]);
        }
        newPages[currentPageIndex] = filteredRecords;
        return newPages;
      });
    }

    setNextPageToken(Documents.next_page ?? null);

    if (Documents.next_page) {
      setPageTokens((prev) => {
        if (prev.includes(Documents.next_page)) return prev;
        return [...prev, Documents.next_page];
      });
    }
  }, [
    Documents,
    currentPageIndex,
    currentPageToken,
    isPlaceholderData,
    isError,
  ]);

  const aggregatedDocuments = useMemo(() => {
    const flat = pages
      .slice(0, currentPageIndex + 1)
      .flat()
      .filter((doc) => doc && doc.id);
    const seen = new Set();
    return flat.filter((doc) => {
      if (seen.has(doc.id)) return false;
      seen.add(doc.id);
      return true;
    });
  }, [pages, currentPageIndex]);

  const hasPartialResults =
    initialTotalCount != null &&
    initialTotalCount > 0 &&
    aggregatedDocuments.length < initialTotalCount;

  const markerLocations = useMemo(() => {
    return aggregatedDocuments
      .filter(
        (doc) =>
          doc.id &&
          doc.site &&
          Number.isFinite(doc.site.latitude) &&
          Number.isFinite(doc.site.longitude)
      )
      .map((doc) => ({
        key: doc.id,
        location: {
          lat: doc.site.latitude,
          lng: doc.site.longitude,
        },
      }));
  }, [aggregatedDocuments]);

  // Fetch document details when navigating from notification
  useEffect(() => {
    const workPermitId = navigationWorkPermitId || selectedWorkPermitId;
    if (
      workPermitId &&
      !fetchedWorkPermitIds.current.has(workPermitId) &&
      !loadingStates[workPermitId]
    ) {
      const fetchDocument = async () => {
        fetchedWorkPermitIds.current.add(workPermitId);
        setLoadingStates((prev) => ({ ...prev, [workPermitId]: true }));
        try {
          const data = await getDetailledData(workPermitId);
          setSelectedDocument(data);
        } catch (error) {
          handleApiError(error);
          const apiMsg = error.response?.data?.message;
          const fullMessage = apiMsg
            ? `${t("documentsPage.errorLoadDetails")}: ${apiMsg}`
            : t("documentsPage.errorLoadDetails");

          setToast({
            open: true,
            severity: "error",
            message: fullMessage,
          });
        } finally {
          setLoadingStates((prev) => ({ ...prev, [workPermitId]: false }));
          clearSelectedWorkPermitId();
          if (navigationWorkPermitId) {
            navigate("/docs", { state: null, replace: true });
          }
          fetchedWorkPermitIds.current.delete(workPermitId);
        }
      };
      fetchDocument();
    }
  }, [
    navigationWorkPermitId,
    selectedWorkPermitId,
    getDetailledData,
    handleApiError,
    setToast,
    clearSelectedWorkPermitId,
    navigate,
    loadingStates,
    t,
  ]);

  // Handle direct URL access to documents
  useEffect(() => {
    if (
      urlDocumentId &&
      handledUrlDocumentId.current !== urlDocumentId &&
      !selectedDocument &&
      !navigationWorkPermitId &&
      !selectedWorkPermitId &&
      !loadingStates[urlDocumentId]
    ) {
      handledUrlDocumentId.current = urlDocumentId;
      const fetchDocument = async () => {
        setLoadingStates((prev) => ({ ...prev, [urlDocumentId]: true }));
        try {
          const data = await getDetailledData(urlDocumentId);
          setSelectedDocument(data);
          // Don't call navigate here since we're already at the right URL
        } catch (error) {
          const apiMsg = error.response?.data?.message;
          const fullMessage = apiMsg
            ? `${t("documentsPage.errorLoadDetails")}: ${apiMsg}`
            : t("documentsPage.errorLoadDetails");

          setToast({
            open: true,
            severity: "error",
            message: fullMessage,
          });
          // Navigate back to docs if direct URL access fails
          navigate("/docs", { replace: true });
        } finally {
          setLoadingStates((prev) => ({ ...prev, [urlDocumentId]: false }));
        }
      };
      fetchDocument();
    }
  }, [
    urlDocumentId,
    selectedDocument,
    navigationWorkPermitId,
    selectedWorkPermitId,
    loadingStates,
    getDetailledData,
    setToast,
    t,
    navigate,
  ]);

  const handleStatusClick = (status) => {
    // Reset pagination first
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);

    setSelectedStatuses((prev) => {
      const isSelected = prev.some((s) => s.name === status.name);
      if (isSelected) {
        return prev.filter((s) => s.name !== status.name);
      }
      return [...prev, status];
    });
  };

  const handleOpenCalendar = () => {
    setCalendarDialogOpen(true);
  };
  const handleCloseCalendar = () => {
    setCalendarDialogOpen(false);
  };

  const handleCalendarEventClick = (document) => {
    setSummaryDocument(document);
    setSummaryPopupOpen(true);
  };

  const handleCloseSummaryPopup = () => {
    setSummaryPopupOpen(false);
    setSummaryDocument(null);
  };

  const handleMapMarkerClick = async (documentId) => {
    // Close map and open document detail
    handleCloseMapDialog();
    handleDetailClick(documentId);
  };

  const handleTodayFilter = () => {
    const today = new Date();

    // Format as YYYY-MM-DD for the date inputs
    const formatDateOnly = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const todayString = formatDateOnly(today);
    setFromDate(todayString);
    setToDate(todayString);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
    setIsTodayFilterActive(true);
    setTodayFilterClicked((c) => c + 1);
  };

  const handleSiteChange = (selectedOptions) => {
    setSelectedSites(selectedOptions || []);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleWorkflowChange = (selectedOptions) => {
    setSelectedWorkflows(selectedOptions || []);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
  };

  const handleResetFilters = () => {
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setInputValue("");
    setSelectedSites([]);
    setSelectedWorkflows([]);
    setSelectedStatuses([]);
    setPageTokens([null]);
    setPages([]);
    setCurrentPageIndex(0);
    setInitialTotalCount(null);
    setIsTodayFilterActive(false);
    // Only navigate to docs if we're not viewing a specific document
    if (!urlDocumentId) {
      navigate("/docs", { replace: true });
    }
  };

  const handleNextPage = () => {
    if (!nextPageToken || isFetching) return;

    const slotEmpty =
      !pages[currentPageIndex] || pages[currentPageIndex]?.length === 0;
    if (currentPageIndex > 0 && slotEmpty && isError) {
      refetch();
      return;
    }

    const nextIdx = currentPageIndex + 1;
    if (nextIdx >= pageTokens.length) return;

    setCurrentPageIndex(nextIdx);
  };

  const handleDetailClick = async (documentId) => {
    setLoadingStates((prev) => ({ ...prev, [documentId]: true }));
    try {
      const data = await getDetailledData(documentId);
      setSelectedDocument(data);
      // Update URL to reflect the opened document
      navigate(`/doc/${documentId}`, { replace: true });
    } catch (error) {
      // Grab whichever message the API returned

      const apiMsg = error.response?.data?.message;

      // Your generic translation, then the API text
      const fullMessage = apiMsg
        ? `${t("documentsPage.errorLoadDetails")}: ${apiMsg}`
        : t("documentsPage.errorLoadDetails");

      setToast({
        open: true,
        severity: "error",
        message: fullMessage,
      });
    } finally {
      setLoadingStates((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleDeleteClick = (docId) => {
    setItemToDelete(docId);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (itemToDelete) {
      setLoadingStates((prev) => ({ ...prev, [itemToDelete]: true }));
      try {
        await deleteData(itemToDelete);
        setToast({
          open: true,
          severity: "success",
          message: t("documentsPage.deleteSuccess"),
        });
        setPageTokens([null]);
        setPages([]);
        setCurrentPageIndex(0);
        refetch();
      } catch (err) {
        const apiMsg = err.response?.data?.message;
        const fullMsg = apiMsg
          ? `${t("documentsPage.deleteError")}: ${apiMsg}`
          : t("documentsPage.deleteError");

        setToast({
          open: true,
          severity: "error",
          message: fullMsg,
        });
      } finally {
        setLoadingStates((prev) => ({ ...prev, [itemToDelete]: false }));
        setItemToDelete(null);
        setDeleteModalOpen(false);
      }
    }
  };

  const handleDocumentDetailClose = useCallback(() => {
    setSelectedDocument(null);
    clearSelectedWorkPermitId();
    handledUrlDocumentId.current = null;
    // Navigate back to docs page when closing document detail
    if (urlDocumentId) {
      navigate("/docs", { replace: true });
    }
  }, [clearSelectedWorkPermitId, urlDocumentId, navigate]);

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true);
  };

  const handleCloseMapDialog = () => {
    setMapDialogOpen(false);
  };

  const handleOpenSimopsDialog = () => {
    setSimopsDialogOpen(true);
  };

  const handleCloseSimopsDialog = () => {
    setSimopsDialogOpen(false);
  };

  // Group active permits by site for SIMOPS
  const simopsData = useMemo(() => {
    const activePermits = aggregatedDocuments.filter((doc) => !doc.is_final);
    const groupedBySite = {};

    activePermits.forEach((doc) => {
      const siteName = doc.site?.name || "Unknown Site";
      if (!groupedBySite[siteName]) {
        groupedBySite[siteName] = [];
      }
      groupedBySite[siteName].push(doc);
    });

    return groupedBySite;
  }, [aggregatedDocuments]);

  const totalActivePermits = useMemo(() => {
    // Only count permits from sites with 2 or more permits (true SIMOPS)
    return Object.values(simopsData).reduce((sum, permits) => {
      return permits.length >= 2 ? sum + permits.length : sum;
    }, 0);
  }, [simopsData]);

  const hasMoreDocuments = nextPageToken;

  return (
    <>
      <Toast
        open={toast.open}
        onClose={handleToastClose}
        severity={toast.severity}
        message={toast.message}
      />

      {/* 2) Then conditionally render the loading state or the main UI */}
      {isStatusLoading || metadataLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
          borderRadius={2}
        >
          <CircularProgress
            size={48}
            aria-label={t("documentsPage.loadingFiltersAria")}
          />
          <Typography variant="body1" ml={2} color="text.secondary">
            {t("documentsPage.loadingFilters")}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "100vh",
            bgcolor: theme.palette.background.default,
          }}
        >
          {/* Compact Enterprise Header */}
          <DocumentsHeader
            icon="📄"
            title={t("documentsPage.title") || "Documents Management"}
            subtitle={
              t("documentsPage.subtitle") ||
              "Search, filter and manage work permit documents"
            }
            shownCount={aggregatedDocuments.length}
            totalCount={initialTotalCount ?? aggregatedDocuments.length}
            onCalendarClick={handleOpenCalendar}
            onMapClick={handleOpenMapDialog}
            onSimopsClick={handleOpenSimopsDialog}
            simopsCount={totalActivePermits}
            bannerNote={
              hasPartialResults
                ? t("documentsPage.partialResultsNotice")
                : undefined
            }
            t={t}
            translationPrefix="documentsPage"
          />

          {/* Enhanced Filters Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: "100%",
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[1],
              }}
            >
              <Accordion
                sx={{
                  width: "100%",
                  bgcolor: "transparent",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  component="div"
                  sx={{
                    minHeight: 48,
                    height: 48,
                    px: 2,

                    // 1) Flex container, spread items left/right
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  {/* Left side: icon + title */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexGrow: 1,
                      overflow: "hidden",
                    }}
                  >
                    <ExpandMoreIcon sx={{ mr: 1, flexShrink: 0 }} />
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t("documentsPage.searchFilters")}
                    </Typography>
                  </Box>

                  {/* Right side: Today + Reset buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: 0.5, sm: 1 },
                      flexShrink: 1, // Allow shrinking
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      size="small"
                      variant={isTodayFilterActive ? "contained" : "outlined"}
                      color="primary"
                      onClick={handleTodayFilter}
                      startIcon={<TodayIcon fontSize="small" />}
                      sx={{
                        minWidth: { xs: 40, sm: "auto" },
                        px: { xs: 1, sm: 2 },
                        "& .MuiButton-startIcon": {
                          marginRight: { xs: 0, sm: 1 },
                        },
                      }}
                    >
                      <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                        {t("documentsPage.todayFilter")}
                      </Box>
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResetFilters}
                      startIcon={<RefreshIcon fontSize="small" />}
                      disabled={
                        !fromDate &&
                        !toDate &&
                        !searchTerm &&
                        selectedSites.length === 0 &&
                        selectedWorkflows.length === 0 &&
                        selectedStatuses.length === 0
                      }
                      sx={{
                        minWidth: { xs: 40, sm: "auto" },
                        px: { xs: 1, sm: 2 },
                        "& .MuiButton-startIcon": {
                          marginRight: { xs: 0, sm: 1 },
                        },
                      }}
                    >
                      <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                        {t("documentsPage.resetFilters")}
                      </Box>
                    </Button>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label={t("documentsPage.fromDateLabel")}
                            type="date"
                            size="normal"
                            value={fromDate}
                            onChange={(e) => {
                              setFromDate(e.target.value);
                              setIsTodayFilterActive(false);
                              setPageTokens([null]);
                              setPages([]);
                              setCurrentPageIndex(0);
                              setInitialTotalCount(null);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            label={t("documentsPage.toDateLabel")}
                            type="date"
                            size="normal"
                            value={toDate}
                            onChange={(e) => {
                              setToDate(e.target.value);
                              setIsTodayFilterActive(false);
                              setPageTokens([null]);
                              setPages([]);
                              setCurrentPageIndex(0);
                              setInitialTotalCount(null);
                            }}
                            slotProps={{ inputLabel: { shrink: true } }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <TextField
                        label={t("documentsPage.searchTermLabel")}
                        size="normal"
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          setSearchTerm(e.target.value);
                          setPageTokens([null]);
                          setPages([]);
                          setCurrentPageIndex(0);
                          navigate(
                            `/docs?search=${encodeURIComponent(e.target.value)}`,
                            { replace: true }
                          );
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Select
                        isMulti
                        components={animatedComponents}
                        options={SitesList}
                        value={selectedSites}
                        onChange={handleSiteChange}
                        placeholder={t("documentsPage.selectSites")}
                        isClearable
                        styles={{
                          ...multiSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Select
                        isMulti
                        components={animatedComponents}
                        options={documentWorkflowOptions}
                        value={selectedWorkflows}
                        onChange={handleWorkflowChange}
                        placeholder={t("documentsPage.selectWorkType")}
                        isClearable
                        styles={{
                          ...multiSelectStyles,
                          menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>

          {/* Enhanced Status Filters */}
          <Box
            sx={{
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
              p: 2,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[1],
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              {t("documentsPage.statusFilters") || "Status Filters"}
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {statusButtons.map((status) => {
                const isSelected = selectedStatuses.some(
                  (s) => s.name === status.name
                );
                const isLoading = isFetching && isSelected;

                return (
                  <Button
                    key={status.name}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => handleStatusClick(status)}
                    disabled={isLoading}
                    sx={{
                      minWidth: 120,
                      height: 48,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      position: "relative",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: theme.shadows[4],
                      },
                      ...(isSelected && {
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        boxShadow: theme.shadows[2],
                      }),
                      ...(isLoading && {
                        opacity: 0.7,
                      }),
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="inherit"
                          sx={{ fontWeight: "inherit" }}
                        >
                          {status.name}
                        </Typography>
                        <Chip
                          label={status.count}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            bgcolor: isSelected
                              ? alpha(theme.palette.primary.contrastText, 0.2)
                              : alpha(theme.palette.primary.main, 0.1),
                            color: isSelected
                              ? theme.palette.primary.contrastText
                              : theme.palette.primary.main,
                            border: "none",
                          }}
                        />
                      </Stack>
                    )}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {isLoading && pages.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "50vh",
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                p: 4,
              }}
            >
              <CircularProgress
                size={64}
                color="primary"
                aria-label={t("documentsPage.loadingDocumentsAria")}
              />
              <Typography
                variant="h6"
                sx={{
                  mt: 2,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("documentsPage.loadingDocuments") || "Loading Documents..."}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("documentsPage.loadingDescription") ||
                  "Please wait while we fetch your documents"}
              </Typography>
            </Box>
          )}

          {error && aggregatedDocuments.length === 0 && !isFetching && (
            <Box
              sx={{
                p: 4,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {t("documentsPage.errorTitle") || "Error Loading Documents"}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {t("documentsPage.errorMessage") ||
                  "Please try again later or contact support."}
              </Typography>
              <Button
                variant="contained"
                color="error"
                onClick={() => refetch()}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {t("documentsPage.retryButton") || "Retry"}
              </Button>
            </Box>
          )}

          {error && aggregatedDocuments.length > 0 && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => refetch()}
                  sx={{ fontWeight: 600, textTransform: "none" }}
                >
                  {t("documentsPage.retryButton") || "Retry"}
                </Button>
              }
            >
              {t("documentsPage.loadMoreErrorInline")}
            </Alert>
          )}

          <CalendarDialog
            open={calendarDialogOpen}
            onClose={handleCloseCalendar}
            documents={aggregatedDocuments}
            onSelectEvent={handleCalendarEventClick}
            t={t}
            i18nLanguage={i18n.language}
            translationPrefix="documentsPage"
          />

          <DocumentSummaryPopup
            open={summaryPopupOpen}
            onClose={handleCloseSummaryPopup}
            document={summaryDocument}
            onViewDetails={handleDetailClick}
            t={t}
          />

          {aggregatedDocuments.length === 0 && !isLoading && !error && (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 4,
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: 64,
                  color: theme.palette.text.disabled,
                  mb: 2,
                }}
              >
                📄
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}
              >
                {t("documentsPage.noDocumentsTitle") || "No Documents Found"}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {t("documentsPage.noDocumentsMessage") ||
                  "No documents match your current filters. Try adjusting your search criteria."}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                }}
              >
                {t("documentsPage.clearFilters") || "Clear Filters"}
              </Button>
            </Box>
          )}

          {aggregatedDocuments.length > 0 && (
            <Box>
              <Grid container spacing={3}>
                {aggregatedDocuments.map((doc) => (
                  <Grid
                    size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                    key={doc.id}
                  >
                    <DocumentCard
                      doc={doc}
                      onDetailClick={handleDetailClick}
                      onDeleteClick={handleDeleteClick}
                      isLoading={!!loadingStates[doc.id]}
                      showDeleteButton={session?.user_type === "ORG_ADMIN"}
                      showCreatedAt={true}
                      showBusinessIcon={false}
                      t={t}
                      translationPrefix="documentsPage"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {hasMoreDocuments && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 4,
                p: 3,
                bgcolor: theme.palette.background.paper,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleNextPage}
                disabled={!nextPageToken || isFetching}
                aria-label={t("documentsPage.loadMoreAria")}
                startIcon={
                  isFetching ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  boxShadow: theme.shadows[2],
                  "&:hover": {
                    boxShadow: theme.shadows[4],
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isFetching
                  ? t("documentsPage.loading") || "Loading..."
                  : t("documentsPage.loadMore") || "Load More Documents"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {selectedDocument && (
        <DocumentDetail
          workPermit={selectedDocument}
          onClose={handleDocumentDetailClose}
          onActionSuccess={() => {
            setPageTokens([null]);
            setPages([]);
            setCurrentPageIndex(0);
            queryClient.invalidateQueries(["documents"]);
            setToast({
              open: true,
              severity: "success",
              message: t("documentsPage.actionSuccess"),
            });
            // Close the document detail and navigate back to docs
            setSelectedDocument(null);
            handledUrlDocumentId.current = null;
            if (urlDocumentId) {
              navigate("/docs", { replace: true });
            }
          }}
        />
      )}

      <MapDialog
        open={mapDialogOpen}
        onClose={handleCloseMapDialog}
        documents={aggregatedDocuments}
        markerLocations={markerLocations}
        onMarkerClick={handleMapMarkerClick}
        mapsLoaded={mapsLoaded}
        setMapsLoaded={setMapsLoaded}
        t={t}
        translationPrefix="documentsPage"
      />

      <DeleteModal
        isDeleteModalOpen={deleteModalOpen}
        setDeleteIsModalOpen={setDeleteModalOpen}
        handleDeleteItem={handleConfirmDelete}
        itemType={t("documentsPage.itemTypePTW")}
      />

      <SimopsDialog
        open={simopsDialogOpen}
        onClose={handleCloseSimopsDialog}
        simopsData={simopsData}
        onPermitClick={(permitId) => {
          handleDetailClick(permitId);
          handleCloseSimopsDialog();
        }}
        t={t}
        isXs={isXs}
      />
    </>
  );
}

export default DocumentsPage;
